import type { CSSProperties } from 'react'
import { lazy, Suspense, useEffect } from 'react'
import { createBrowserRouter, Navigate, useRouteError, isRouteErrorResponse, useNavigate } from 'react-router-dom'
import { AppShell } from '@/shared/components/layout/AppShell'
import { ErrorBoundary, PageLock, PageLoadingState, ScopeGate } from '@/shared/components'

import { QueueView } from '@/features/queue'
import { WelcomePage } from '@/features/welcome'
import { useSettingsStore } from '@/shared/stores/settings-store'
import { IS_TAURI } from '@/lib/platform'

/**
 * Whether a path is served by this build's router.
 *
 * Only the first segment is checked, which is all the callers need: they are
 * guarding against a whole feature being absent, not a bad deep link.
 */
function routeExists(path: string): boolean {
  const segment = path.replace(/^\//, '').split('/')[0] ?? ''
  if (segment === '') return true
  return IS_TAURI || !isDesktopOnly(segment)
}

/**
 * Backward-compat shim: navigating to /settings opens the modal overlay
 * and redirects the user back to /events so the modal appears over content.
 */
function SettingsRedirect() {
  const navigate = useNavigate()
  const openSettings = useSettingsStore((s) => s.openSettings)
  useEffect(() => {
    openSettings('general')
    // /events is desktop-only; the browser portal's equivalent landing spot is
    // the queue. Redirecting to a route this build does not serve would leave
    // the settings modal floating over a 404 (Task 4093).
    navigate(IS_TAURI ? '/events' : '/queue', { replace: true })
  }, [openSettings, navigate])
  return null
}

/**
 * Landing page redirector — runs at `/` and routes the user to whichever
 * landing page they've configured in Settings → Startup. Defaults to the
 * Kodeon splash placeholder.
 */
function LandingRedirect() {
  const landingPage = useSettingsStore((s) => s.landingPage)
  const configured =
    landingPage.kind === 'kodeon-splash' ? '/welcome' : landingPage.route
  // The configured landing page is persisted per operator and may name a route
  // this build does not ship — either a desktop-only page in the browser
  // portal, or a stale value from a previous version. Landing on a path the
  // router cannot resolve would drop the operator straight onto the 404, so
  // fall back to /welcome, which exists in both builds (Task 4093).
  const target = routeExists(configured) ? configured : '/welcome'
  return <Navigate to={target} replace />
}

// ── Lazy-loaded Stage 5 features ──────────────────────────────────────────────
// Each feature is code-split via React.lazy() to avoid bundling all 22 features
// into the initial chunk. Uses .then(m => ({ default: m.X })) for named exports.

/**
 * `lazy()` for a route that exists only in the desktop build (Task 4093).
 *
 * Gating the route *entry* is not enough on its own. These `const X = lazy(...)`
 * declarations are top-level, so the `import()` inside them stays in the module
 * graph even when the route referencing X is filtered out — Rollup emits the
 * chunk, and any `@tauri-apps` module that feature reaches comes with it. That
 * is precisely how `@tauri-apps/api/core` leaked into a first pass of this
 * build, via `features/skills/lib/driveApi`.
 *
 * Collapsing to a null-rendering placeholder in the web build removes the only
 * reference to the loader, so the whole subtree is dropped. The placeholder is
 * unreachable there in any case: the routes that would render it are filtered
 * out of the router below.
 */
export function desktopLazy(
  loader: () => Promise<{ default: React.ComponentType }>,
): React.LazyExoticComponent<React.ComponentType> {
  if (!IS_TAURI) {
    return lazy(() => Promise.resolve({ default: () => null }))
  }
  return lazy(loader)
}

// Eagerly imported until Task 4093's review: these four features are all in
// DESKTOP_ONLY_PREFIXES, but a static import ships the whole module graph
// regardless of whether any route referencing it survives the filter — so
// `memory` (14/15 source files, including a working AddMemoryModal and the
// /v1/agents/{id}/memories client), `events`, `agents` and `log-feature` were
// all reaching the browser bundle. Routing them through desktopLazy is what
// actually removes them; the route filter alone never could.
const EventStreamView = desktopLazy(() => import('@/features/events').then(m => ({ default: m.EventStreamView })))
const MemoryPage = desktopLazy(() => import('@/features/memory').then(m => ({ default: m.MemoryPage })))
const AgentsOverview = desktopLazy(() => import('@/features/agents').then(m => ({ default: m.AgentsOverview })))
const AgentDetail = desktopLazy(() => import('@/features/agents').then(m => ({ default: m.AgentDetail })))
const LiveSessionsView = desktopLazy(() => import('@/features/agents').then(m => ({ default: m.LiveSessionsView })))
const LiveSessionDetail = desktopLazy(() => import('@/features/agents').then(m => ({ default: m.LiveSessionDetail })))
// `health` ships in BOTH targets (Task 4095). It is the deep-link target of the
// shipped Google Chat and email health alerts, whose CTA is
// `https://stream.kodeon.ai/health` — an operator arrives cold from an email
// client with no client-side history, so the route must exist in the browser
// build or the alert leads to a 404. The feature has zero Tauri coupling (no
// `@tauri-apps`, no `lib/tauri`, no `tauriInvoke`), so plain `lazy` is safe.
// `email-health` is a different feature and stays desktop-only.
//
// The route keeps its `ScopeGate required="infra:read"`, so un-gating the
// bundle does not widen who can read the page — only which build serves it.
const HealthPage = lazy(() => import('@/features/health').then(m => ({ default: m.HealthPage })))
// `insights` ships in BOTH targets (this task). Overview and Acquisition read
// the live Apple ASC and revenue contract tables through the gateway's
// /v1/admin/insights/* routes, and the people who read those numbers work in a
// browser, not on the Mac that runs the desktop build — so plain `lazy`, NOT
// `desktopLazy`. `desktopLazy` collapses to a null component in the web build,
// which drops the module graph and would ship an EMPTY page to the portal
// rather than no page at all.
//
// The feature has zero Tauri coupling: it reaches only `@/lib/api` (a plain
// fetch client) and `@/shared/components/charts`. `pnpm verify:web` is the
// gate that keeps it that way. Correspondingly, `insights` must NOT be added
// to DESKTOP_ONLY_PREFIXES below — the omission is the action.
//
// Each of the six pages is its own lazy component and its own route, because
// the page-lock key is the `pageId` on <LazyRoute>. That is what lets Overview
// and Acquisition ship unlocked while the four scaffolded pages stay behind
// the manifest (ADR-25 §7.2).
const InsightsLayout = lazy(() => import('@/features/insights').then(m => ({ default: m.InsightsLayout })))
const InsightsOverviewPage = lazy(() => import('@/features/insights').then(m => ({ default: m.OverviewPage })))
const InsightsAcquisitionPage = lazy(() => import('@/features/insights').then(m => ({ default: m.AcquisitionPage })))
const InsightsEngagementPage = lazy(() => import('@/features/insights').then(m => ({ default: m.EngagementPage })))
const InsightsMonetisationPage = lazy(() => import('@/features/insights').then(m => ({ default: m.MonetisationPage })))
const InsightsExplorerPage = lazy(() => import('@/features/insights').then(m => ({ default: m.CustomerExplorerPage })))
const InsightsCohortBuilderPage = lazy(() => import('@/features/insights').then(m => ({ default: m.CohortBuilderPage })))
const CustomersPage = lazy(() => import('@/features/customers').then(m => ({ default: m.CustomersPage })))
const CustomerDetailPage = lazy(() => import('@/features/customers').then(m => ({ default: m.CustomerDetailPage })))
const CustomerPage = lazy(() => import('@/features/customers').then(m => ({ default: m.CustomerPage })))
const DeletionsPage = lazy(() => import('@/features/customers').then(m => ({ default: m.DeletionsPage })))

const SessionsBrowserPage = desktopLazy(() => import('@/features/sessions').then(m => ({ default: m.SessionsBrowserPage })))
const AgentAuditPage = desktopLazy(() => import('@/features/agent-audit').then(m => ({ default: m.AgentAuditPage })))
const ExecutionAuditPage = desktopLazy(() => import('@/features/agent-audit').then(m => ({ default: m.ExecutionAuditPage })))
const PlaygroundPage = desktopLazy(() => import('@/features/agents/playground').then(m => ({ default: m.PlaygroundPage })))
const PlaygroundIndexPage = desktopLazy(() => import('@/features/agents/playground').then(m => ({ default: m.PlaygroundIndexPage })))
// `automations` ships in BOTH targets, for parity with the desktop app: the
// Automations list, Runs (live / previous / awaiting approval), the run page
// and execution detail. Plain `lazy`, NOT `desktopLazy` — the latter collapses
// to a null component in the web build and would ship an empty page.
//
// The feature has zero Tauri coupling: it reaches only `@/lib/api`, shared UI,
// and the queue / inbox-manager stores and API clients the browser build
// already serves. One control degrades in the browser: a step's "View Session"
// hands off to AgentSessionModal, which renders sessions in a pop-out window
// that only the desktop build has, so the portal shows the "open Stream on the
// Mac app" toast instead of the transcript. `pnpm verify:web` is the gate that
// keeps the feature Tauri-free, and its check 4b fails if these pages blank.
// Correspondingly, `automations` must NOT be in DESKTOP_ONLY_PREFIXES below —
// the omission is the action. Every route keeps its
// `ScopeGate required="operations:admin"`, so this changes which build serves
// the pages, not who can use them.
const AutomationsPage = lazy(() => import('@/features/automations').then(m => ({ default: m.AutomationsPage })))
const AutomationRunPage = lazy(() => import('@/features/automations').then(m => ({ default: m.AutomationRunPage })))
const ExecutionDetailPage = lazy(() => import('@/features/automations').then(m => ({ default: m.ExecutionDetailPage })))
const RunsPage = lazy(() => import('@/features/automations').then(m => ({ default: m.RunsPage })))
const CatalogPage = desktopLazy(() => import('@/features/catalog').then(m => ({ default: m.CatalogPage })))
const OfferingTemplatesPage = desktopLazy(() => import('@/features/offering-templates').then(m => ({ default: m.OfferingTemplatesPage })))
const PurchasesPage = lazy(() => import('@/features/purchases').then(m => ({ default: m.PurchasesPage })))
const SignUpsPage = desktopLazy(() => import('@/features/signups').then(m => ({ default: m.SignUpsPage })))
const InboxPage = lazy(() => import('@/features/inbox-manager').then(m => ({ default: m.InboxPage })))
const EmailPage = desktopLazy(() => import('@/features/email-templates/components/EmailPage').then(m => ({ default: m.EmailPage })))
const TemplateEditPage = desktopLazy(() => import('@/features/email-templates/edit/TemplateEditPage').then(m => ({ default: m.TemplateEditPage })))
const EngDashboardPage = desktopLazy(() => import('@/features/eng-dashboard').then(m => ({ default: m.EngDashboardPage })))
const ArtifactsPage = desktopLazy(() => import('@/features/artifacts').then(m => ({ default: m.ArtifactsPage })))
const DeferredPage = desktopLazy(() => import('@/features/deferred').then(m => ({ default: m.DeferredPage })))
const AgentCostPage = desktopLazy(() => import('@/features/agent-cost').then(m => ({ default: m.AgentCostPage })))
const IntegrationsPage = desktopLazy(() => import('@/features/integrations').then(m => ({ default: m.IntegrationsPage })))
const EmailHealthPage = desktopLazy(() => import('@/features/email-health').then(m => ({ default: m.EmailHealthPage })))
const ReviewsPage = lazy(() => import('@/features/reviews').then(m => ({ default: m.ReviewsPage })))
const LogFeaturePage = desktopLazy(() => import('@/features/log-feature').then(m => ({ default: m.LogFeaturePage })))
const SkillsLibraryPage = desktopLazy(() => import('@/features/skills').then(m => ({ default: m.SkillsLibraryPage })))
const SkillDetailPage = desktopLazy(() => import('@/features/skills').then(m => ({ default: m.SkillDetailPage })))
const StaffListPage = desktopLazy(() => import('@/features/staff').then(m => ({ default: m.StaffListPage })))
const ReportsPage = desktopLazy(() => import('@/features/reports').then(m => ({ default: m.ReportsPage })))

/**
 * Suspense wrapper for lazy-loaded route components.
 * Uses null fallback — the AppShell chrome remains visible during loads.
 * Wrapped in ErrorBoundary to prevent a single lazy feature crash from
 * replacing the entire shell (Task 2830 / WEB-012).
 *
 * Optional `pageId` opts the route into the page-lock system. When set, the
 * route will render the lock overlay (or pass through with a warning
 * indicator) according to `src/lib/page-locks/`. When absent the wrapper is
 * a no-op for routes that are fully released.
 */
function LazyRoute({
  component: Component,
  pageId,
}: {
  component: React.LazyExoticComponent<React.ComponentType>
  pageId?: string
}) {
  const content = (
    <ErrorBoundary>
      <Suspense fallback={<PageLoadingState />}>
        <Component />
      </Suspense>
    </ErrorBoundary>
  )

  if (pageId) {
    return <PageLock pageId={pageId}>{content}</PageLock>
  }

  return content
}

/**
 * Route element for one Insights sub-page.
 *
 * Composes the shared Insights shell (header, filter bar, tab strip) around a
 * single page, and puts the page-lock INSIDE the shell rather than around it.
 *
 * That nesting is deliberate. The lock overlay blurs and inerts whatever it
 * wraps, and an operator who lands on a locked Insights page still needs the
 * tab strip to navigate to one of the two live pages. Wrapping the whole
 * shell would blur the only way out.
 *
 * `pageId` is per-page (`insights-engagement`, …), which is what gives
 * per-page lock granularity from a single manifest.
 */
function InsightsRoute({
  page,
  pageId,
}: {
  page: React.LazyExoticComponent<React.ComponentType>
  pageId?: string
}) {
  return (
    <ErrorBoundary>
      <Suspense fallback={<PageLoadingState />}>
        <InsightsLayout>
          <LazyRoute component={page} pageId={pageId} />
        </InsightsLayout>
      </Suspense>
    </ErrorBoundary>
  )
}

const errorStyles: Record<string, CSSProperties> = {
  container: {
    display: 'flex',
    height: '100%',
    alignItems: 'center',
    justifyContent: 'center',
  },
  content: {
    textAlign: 'center',
  },
  title: {
    fontSize: '22px',
    fontWeight: 600,
    color: 'var(--text-primary)',
  },
  message: {
    marginTop: '8px',
    color: 'var(--text-muted)',
  },
  link: {
    marginTop: '16px',
    display: 'inline-block',
    color: 'var(--accent-primary)',
  },
  button: {
    marginTop: '16px',
    borderRadius: 'var(--radius-md)',
    background: 'var(--accent-primary)',
    padding: '8px 16px',
    color: 'white',
    border: 'none',
    cursor: 'pointer',
  },
}

/**
 * Route error boundary for handling navigation errors and 404s.
 */
function RouteErrorBoundary() {
  const error = useRouteError()

  if (isRouteErrorResponse(error)) {
    if (error.status === 404) {
      return (
        <div style={errorStyles.container}>
          <div style={errorStyles.content}>
            <h2 style={errorStyles.title}>Page Not Found</h2>
            <p style={errorStyles.message}>
              The page you're looking for doesn't exist.
            </p>
            <a href="/events" style={errorStyles.link} className="link-hover">
              Return to Events
            </a>
          </div>
        </div>
      )
    }
  }

  // For other errors, show generic message
  const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred'

  return (
    <div style={errorStyles.container}>
      <div style={errorStyles.content}>
        <h2 style={errorStyles.title}>Something went wrong</h2>
        <p style={errorStyles.message}>{errorMessage}</p>
        <button
          onClick={() => window.location.reload()}
          style={errorStyles.button}
          className="button-primary"
        >
          Reload Page
        </button>
      </div>
    </div>
  )
}

/**
 * Routes present only in the Tauri desktop build (Task 4093).
 *
 * The browser portal ships the six support surfaces named in the plan —
 * Attention Queue, E-Mail Inbox, Review Inbox, Customers, Purchases (refunds)
 * and Customer Timeline — plus the chrome they need (welcome, settings,
 * catch-all). Everything else is an engineering tool that stays on the Mac,
 * except for the scope additions recorded in Task 4095: `health`, `insights`
 * and `automations` (Automations + Runs) also ship in the browser build.
 *
 * Listed by path prefix rather than by editing the route array in two places,
 * so the desktop route table below remains the single definition and the split
 * is one reviewable list.
 *
 * This is a build-time exclusion, not a runtime redirect. `IS_TAURI` folds to
 * `false` in the web build, so Rollup drops these entries — and with them the
 * `lazy(() => import(...))` calls that are the only references to the Reports,
 * Skills, Claude-sessions and Playground feature modules. That is what keeps
 * `@tauri-apps` out of the browser bundle: those four features hold every
 * remaining static Tauri import in `src/`.
 *
 * Consequence worth stating plainly: a desktop-only path in the browser build
 * does not 403 or redirect — it does not resolve at all, and falls through to
 * the shell's catch-all 404. That is the intended behaviour (the acceptance
 * criterion is "absent from the router"), and it is also the honest one: the
 * code to serve those pages genuinely is not in the bundle.
 */
const DESKTOP_ONLY_PREFIXES = [
  'deferred',
  'memory',
  'agents',
  'executions',
  'playground',
  'agent-cost',
  'sessions',
  'signups',
  'catalog',
  'offering-templates',
  'email-templates',
  'integrations',
  'email-health',
  'engineering',
  'artifacts',
  'log-feature',
  'reports',
  'skills',
  'dev/skills',
  'staff',
  'events',
] as const

/** True when `path` is a desktop-only route (exact segment-prefix match). */
function isDesktopOnly(path: string | undefined): boolean {
  if (!path) return false
  return DESKTOP_ONLY_PREFIXES.some(
    (prefix) => path === prefix || path.startsWith(`${prefix}/`),
  )
}

const desktopRoutes = [
      {
        index: true,
        element: <LandingRedirect />,
      },
      // ── Welcome / Kodeon splash ──────────────────────────────────────────
      // Empty content area; the visual is the particle 'KODEON' rendered by
      // StreamParticleCanvas. Pick any sidebar item to navigate away.
      {
        path: 'welcome',
        element: <WelcomePage />,
      },
      // ── Observe ──────────────────────────────────────────────────────────
      {
        path: 'events',
        element: (
          <ScopeGate required="events:read">
            <LazyRoute component={EventStreamView} />
          </ScopeGate>
        ),
      },

      // ── Operate ──────────────────────────────────────────────────────────
      // Queue is a SINGLE unified attention list (Task 4072): every pending
      // item — tool approvals, workflow human reviews, agent text reviews, PR
      // reviews — appears together, with item type driving the card affordance
      // and an optional non-exclusive filter (never a list partition). The
      // Attention/Responses split was removed.
      //
      // The /queue/{view}/{tab} route shape is RETAINED FOR URL BACK-COMPAT
      // ONLY: the `:view` segment ('attention' | 'responses') no longer filters
      // the list — both segments render the same unified QueueView. The legacy
      // /queue/:tab and /queue/{attention,responses} routes redirect into it so
      // existing bookmarks keep working.
      {
        path: 'queue',
        element: <Navigate to="/queue/attention/all" replace />,
      },
      {
        path: 'queue/attention',
        element: <Navigate to="/queue/attention/all" replace />,
      },
      {
        path: 'queue/responses',
        element: <Navigate to="/queue/responses/all" replace />,
      },
      {
        path: 'queue/all',
        element: <Navigate to="/queue/attention/all" replace />,
      },
      {
        path: 'queue/my',
        element: <Navigate to="/queue/attention/my" replace />,
      },
      {
        path: 'queue/history',
        element: <Navigate to="/queue/attention/history" replace />,
      },
      {
        // Gated on operations:read, not operations:admin — every /v1/attention
        // route the queue calls requires only operations:read (list/count/
        // history) or operations:write (resolve/assign). operations:admin is a
        // fused superset retained for compatibility.
        //
        // This changes nothing for anyone today: no role bundle or consumer
        // seed grants bare operations:admin, so the only principals passing
        // either form of the gate are wildcard holders, and wildcard
        // short-circuits every gate (scope-check.ts). The narrowing is
        // forward-looking — it lets a bounded role such as
        // head_of_customer_service reach the queue. It matters because
        // Stream's client-side mirror does NOT implement ScopeImplications,
        // so unlike the gateway, operations:admin here would not satisfy
        // operations:read for a role granted the granular pair.
        path: 'queue/:view/:tab',
        element: (
          <ScopeGate required="operations:read">
            <QueueView />
          </ScopeGate>
        ),
      },
      {
        path: 'automations',
        element: (
          <ScopeGate required="operations:admin">
            <LazyRoute component={AutomationsPage} />
          </ScopeGate>
        ),
      },
      // Runs — global view of currently-executing and historic automation
      // runs across all automations.  Defined BEFORE the parameterised
      // ``automations/:id`` route so the literal ``/automations/runs`` URL
      // doesn't get captured as an automation ID.  ``automations/live`` is
      // kept as a redirect for any bookmarks made when this page was
      // live-only.
      {
        path: 'automations/runs',
        element: (
          <ScopeGate required="operations:admin">
            <LazyRoute component={RunsPage} />
          </ScopeGate>
        ),
      },
      {
        path: 'automations/live',
        element: <Navigate to="/automations/runs?tab=live" replace />,
      },
      {
        path: 'automations/:id/run',
        element: (
          <ScopeGate required="operations:admin">
            <LazyRoute component={AutomationRunPage} />
          </ScopeGate>
        ),
      },
      // Execution detail — read-only inspection of a single run (live or
      // historical). Reached from the Runs tab on the automation inspector
      // and from the Live Runs view.
      {
        path: 'automations/:automationId/executions/:executionId',
        element: (
          <ScopeGate required="operations:admin">
            <LazyRoute component={ExecutionDetailPage} />
          </ScopeGate>
        ),
      },
      {
        path: 'deferred',
        element: (
          <ScopeGate required="operations:admin">
            <LazyRoute component={DeferredPage} />
          </ScopeGate>
        ),
      },
      // ── Agents ───────────────────────────────────────────────────────────
      {
        path: 'memory',
        element: (
          <ScopeGate required="agents:admin">
            <LazyRoute component={MemoryPage} />
          </ScopeGate>
        ),
      },
      {
        path: 'memory/:agentId',
        element: (
          <ScopeGate required="agents:admin">
            <LazyRoute component={MemoryPage} />
          </ScopeGate>
        ),
      },
      {
        path: 'agents',
        element: (
          <ScopeGate required="agents:admin">
            <LazyRoute component={AgentsOverview} />
          </ScopeGate>
        ),
      },
      {
        path: 'agents/live',
        element: (
          <ScopeGate required="agents:admin">
            <LazyRoute component={LiveSessionsView} />
          </ScopeGate>
        ),
      },
      {
        path: 'agents/live/:sessionId',
        element: (
          <ScopeGate required="agents:admin">
            <LazyRoute component={LiveSessionDetail} />
          </ScopeGate>
        ),
      },
      {
        path: 'agents/:agentId',
        element: (
          <ScopeGate required="agents:admin">
            <LazyRoute component={AgentDetail} />
          </ScopeGate>
        ),
      },
      {
        path: 'agents/:agentId/sessions',
        element: (
          <ScopeGate required="agents:admin">
            <LazyRoute component={AgentDetail} />
          </ScopeGate>
        ),
      },
      {
        path: 'agents/:agentId/memories',
        element: (
          <ScopeGate required="agents:admin">
            <LazyRoute component={AgentDetail} />
          </ScopeGate>
        ),
      },
      {
        // AgentAuditPage reads useParams<{ id }>(), so the route param must be :id (Task 2563)
        path: 'agents/:id/audit',
        element: (
          <ScopeGate required="agents:admin">
            <LazyRoute component={AgentAuditPage} />
          </ScopeGate>
        ),
      },
      {
        path: 'executions/:id/audit',
        element: (
          <ScopeGate required="agents:admin">
            <LazyRoute component={ExecutionAuditPage} />
          </ScopeGate>
        ),
      },
      {
        // PlaygroundPage reads useParams<{ agentId }>() (Task 2562)
        path: 'agents/:agentId/playground',
        element: (
          <ScopeGate required="agents:admin">
            <LazyRoute component={PlaygroundPage} />
          </ScopeGate>
        ),
      },
      // Top-level Playground entry point — agent picker, then chat.
      // Replaces the legacy right-hand "Stream agents" sidebar.
      {
        path: 'playground',
        element: (
          <ScopeGate required="agents:admin">
            <LazyRoute component={PlaygroundIndexPage} />
          </ScopeGate>
        ),
      },
      {
        path: 'playground/:agentId',
        element: (
          <ScopeGate required="agents:admin">
            <LazyRoute component={PlaygroundPage} />
          </ScopeGate>
        ),
      },
      {
        path: 'agent-cost',
        // pageId opts this route into the page-lock manifest
        // (`src/lib/page-locks/manifest.ts`). Locked while we verify the
        // token-usage → spend connectivity end to end.
        element: (
          <ScopeGate required="agents:admin">
            <LazyRoute component={AgentCostPage} pageId="agent-cost" />
          </ScopeGate>
        ),
      },
      // ── Customers ────────────────────────────────────────────────────────
      // /customer (singular) — single-customer detail-only view with built-in
      // search. Same detail UI as CustomerDetailPage, but with a search bar
      // at the top instead of arriving from the manager list. See
      // features/customers/components/CustomerPage.tsx.
      {
        path: 'customer',
        element: (
          <ScopeGate required="user:read">
            <LazyRoute component={CustomerPage} />
          </ScopeGate>
        ),
      },
      {
        path: 'customer/:customerId',
        element: (
          <ScopeGate required="user:read">
            <LazyRoute component={CustomerPage} />
          </ScopeGate>
        ),
      },
      {
        path: 'customer/:customerId/narrative',
        element: (
          <ScopeGate required="user:read">
            <LazyRoute component={CustomerPage} />
          </ScopeGate>
        ),
      },
      {
        path: 'customer/:customerId/opportunity',
        element: (
          <ScopeGate required="user:read">
            <LazyRoute component={CustomerPage} />
          </ScopeGate>
        ),
      },
      {
        path: 'customer/:customerId/entitlements',
        element: (
          <ScopeGate required="user:read">
            <LazyRoute component={CustomerPage} />
          </ScopeGate>
        ),
      },
      {
        path: 'customer/:customerId/timeline',
        element: (
          <ScopeGate required="user:read">
            <LazyRoute component={CustomerPage} />
          </ScopeGate>
        ),
      },
      {
        path: 'customer/:customerId/edit',
        element: (
          <ScopeGate required="user:read">
            <LazyRoute component={CustomerPage} />
          </ScopeGate>
        ),
      },
      {
        path: 'customer/:customerId/purchases',
        element: (
          <ScopeGate required="user:read">
            <LazyRoute component={CustomerPage} />
          </ScopeGate>
        ),
      },
      {
        path: 'customers',
        element: (
          <ScopeGate required="user:read">
            <LazyRoute component={CustomersPage} />
          </ScopeGate>
        ),
      },
      // Deletions queue — static segment registered before customers/:customerId
      // so it is never captured as a customer ID.
      {
        path: 'customers/deletions',
        element: (
          <ScopeGate required="user:read">
            <LazyRoute component={DeletionsPage} />
          </ScopeGate>
        ),
      },
      {
        path: 'customers/:customerId',
        element: (
          <ScopeGate required="user:read">
            <LazyRoute component={CustomerDetailPage} />
          </ScopeGate>
        ),
      },
      {
        path: 'customers/:customerId/narrative',
        element: (
          <ScopeGate required="user:read">
            <LazyRoute component={CustomerDetailPage} />
          </ScopeGate>
        ),
      },
      {
        path: 'customers/:customerId/opportunity',
        element: (
          <ScopeGate required="user:read">
            <LazyRoute component={CustomerDetailPage} />
          </ScopeGate>
        ),
      },
      {
        path: 'customers/:customerId/entitlements',
        element: (
          <ScopeGate required="user:read">
            <LazyRoute component={CustomerDetailPage} />
          </ScopeGate>
        ),
      },
      {
        path: 'customers/:customerId/timeline',
        element: (
          <ScopeGate required="user:read">
            <LazyRoute component={CustomerDetailPage} />
          </ScopeGate>
        ),
      },
      {
        path: 'customers/:customerId/edit',
        element: (
          <ScopeGate required="user:read">
            <LazyRoute component={CustomerDetailPage} />
          </ScopeGate>
        ),
      },
      {
        path: 'customers/:customerId/purchases',
        element: (
          <ScopeGate required="user:read">
            <LazyRoute component={CustomerDetailPage} />
          </ScopeGate>
        ),
      },
      {
        path: 'customers/:customerId/interactions',
        element: (
          <ScopeGate required="user:read">
            <LazyRoute component={CustomerDetailPage} />
          </ScopeGate>
        ),
      },
      {
        path: 'sessions',
        element: (
          <ScopeGate required="agents:admin">
            <LazyRoute component={SessionsBrowserPage} />
          </ScopeGate>
        ),
      },
      // ── Commerce ─────────────────────────────────────────────────────────
      {
        path: 'purchases',
        element: (
          <ScopeGate required="payments:read">
            <LazyRoute component={PurchasesPage} />
          </ScopeGate>
        ),
      },
      {
        path: 'signups',
        element: (
          <ScopeGate required="payments:read">
            <LazyRoute component={SignUpsPage} />
          </ScopeGate>
        ),
      },
      {
        // pageId opts this route into the page-lock manifest — locked while
        // we investigate why the AppLife IAPs column shows duplicate
        // identifiers for some apps.
        path: 'catalog',
        element: (
          <ScopeGate required="catalog:read">
            <LazyRoute component={CatalogPage} pageId="catalog" />
          </ScopeGate>
        ),
      },
      {
        path: 'offering-templates',
        element: (
          <ScopeGate required="catalog:read">
            <LazyRoute component={OfferingTemplatesPage} />
          </ScopeGate>
        ),
      },
      // ── Communications ───────────────────────────────────────────────────
      {
        // comms:read, not comms:all — the inbox's routes require comms:read to
        // list and comms:write to reply/archive/classify. comms:all is the
        // fused superset kept for compatibility. As with the queue gate above,
        // this is forward-looking: nothing grants bare comms:all today.
        path: 'inbox',
        element: (
          <ScopeGate required="comms:read">
            <LazyRoute component={InboxPage} />
          </ScopeGate>
        ),
      },
      {
        // pageId opts this route into the page-lock manifest — locked while
        // the in-UI preview is not rendering identically to the email as
        // delivered (suspected missing theme overrides in the preview path).
        path: 'email-templates',
        element: (
          <ScopeGate required="comms:all">
            <LazyRoute component={EmailPage} pageId="email-templates" />
          </ScopeGate>
        ),
      },
      {
        path: 'email-templates/:id/edit',
        element: (
          <ScopeGate required="comms:all">
            <LazyRoute component={TemplateEditPage} />
          </ScopeGate>
        ),
      },
      // ── Platform Health ──────────────────────────────────────────────────
      {
        path: 'health',
        element: (
          <ScopeGate required="infra:read">
            <LazyRoute component={HealthPage} />
          </ScopeGate>
        ),
      },
      {
        path: 'integrations',
        // pageId opts this route into the page-lock manifest
        // (`src/lib/page-locks/manifest.ts`). Locked while we finalise the
        // health-checking methodology for partner integrations.
        element: (
          <ScopeGate required="infra:read">
            <LazyRoute component={IntegrationsPage} pageId="integrations" />
          </ScopeGate>
        ),
      },
      {
        path: 'email-health',
        element: (
          <ScopeGate required="infra:read">
            <LazyRoute component={EmailHealthPage} />
          </ScopeGate>
        ),
      },
      // ── Engineering ──────────────────────────────────────────────────────
      {
        path: 'engineering',
        element: (
          <ScopeGate required="engineering:read">
            <LazyRoute component={EngDashboardPage} />
          </ScopeGate>
        ),
      },
      {
        path: 'artifacts',
        element: (
          <ScopeGate required="agents:admin">
            <LazyRoute component={ArtifactsPage} />
          </ScopeGate>
        ),
      },
      {
        // /log-feature is a side-effect route: it opens the LogFeatureModal
        // (mounted globally in App.tsx) and immediately redirects to
        // /engineering so the modal floats over the engineering dashboard.
        // See features/log-feature/components/LogFeaturePage.tsx.
        path: 'log-feature',
        element: (
          <ScopeGate required="engineering:write">
            <LazyRoute component={LogFeaturePage} />
          </ScopeGate>
        ),
      },
      // ── Reviews ──────────────────────────────────────────────────────────
      {
        path: 'reviews',
        element: (
          <ScopeGate required="catalog:read">
            <LazyRoute component={ReviewsPage} />
          </ScopeGate>
        ),
      },
      // ── Insights ─────────────────────────────────────────────────────────
      // Six pages under one shell. Gated on `insights:dashboard:read`
      // LITERALLY — that is the exact scope the gateway requires on all 30
      // /v1/admin/insights/* routes, and Stream's client-side scope mirror
      // does NOT implement the gateway's ScopeImplications. Gating on an
      // admin scope that implies it server-side would lock out every operator
      // holding only the granular grant.
      //
      // Overview and Acquisition carry no `pageId`: they read live contract
      // tables and are released. The other four keep theirs and stay locked.
      {
        path: 'insights',
        element: <Navigate to="/insights/overview" replace />,
      },
      {
        path: 'insights/overview',
        element: (
          <ScopeGate required="insights:dashboard:read">
            <InsightsRoute page={InsightsOverviewPage} />
          </ScopeGate>
        ),
      },
      {
        path: 'insights/acquisition',
        element: (
          <ScopeGate required="insights:dashboard:read">
            <InsightsRoute page={InsightsAcquisitionPage} />
          </ScopeGate>
        ),
      },
      {
        path: 'insights/engagement',
        element: (
          <ScopeGate required="insights:dashboard:read">
            <InsightsRoute page={InsightsEngagementPage} />
          </ScopeGate>
        ),
      },
      {
        path: 'insights/monetisation',
        element: (
          <ScopeGate required="insights:dashboard:read">
            <InsightsRoute page={InsightsMonetisationPage} />
          </ScopeGate>
        ),
      },
      {
        path: 'insights/explorer',
        element: (
          <ScopeGate required="insights:dashboard:read">
            <InsightsRoute page={InsightsExplorerPage} pageId="insights-explorer" />
          </ScopeGate>
        ),
      },
      {
        path: 'insights/cohort-builder',
        element: (
          <ScopeGate required="insights:dashboard:read">
            <InsightsRoute page={InsightsCohortBuilderPage} pageId="insights-cohort-builder" />
          </ScopeGate>
        ),
      },
      // ── Reports Hub ──────────────────────────────────────────────────────
      // /reports is the analytics reports browser: recent reports strip +
      // Google Drive folder navigation. No ScopeGate — Drive access itself
      // is the permission gate (same approach as /skills).
      {
        path: 'reports',
        element: <LazyRoute component={ReportsPage} />,
      },
      // ── Skill Library ────────────────────────────────────────────────────
      // /skills is the top-level library: browse every discovered skill
      // (Stream-bundled + global + project), open one to inspect every file
      // it contains, and download a copy of the entire skill folder to a
      // location the operator picks via a native folder picker. See the
      // implementation in src/features/skills/.
      {
        path: 'skills',
        element: <LazyRoute component={SkillsLibraryPage} />,
      },
      {
        path: 'skills/:scope/:name',
        element: <LazyRoute component={SkillDetailPage} />,
      },
      // /dev/skills was the original SkillsPanel dev route. The library has
      // since been promoted to /skills; we keep this path redirecting so any
      // in-flight bookmarks or links continue to work.
      {
        path: 'dev/skills',
        element: <Navigate to="/skills" replace />,
      },
      // ── Platform Admin ────────────────────────────────────────────────────
      // /staff — Staff Management page. Lists staff members and allows
      // invite/edit/revoke. Requires identity:admin scope (task 3551c).
      // Implemented in task 3552a.
      {
        path: 'staff',
        element: (
          <ScopeGate required="identity:admin">
            <LazyRoute component={StaffListPage} />
          </ScopeGate>
        ),
      },
      // ── Settings ─────────────────────────────────────────────────────────
      // Settings is now a modal overlay (SettingsModal.tsx), not a page.
      // This route exists for backward compat: navigating to /settings
      // opens the modal and redirects to the last viewed page.
      {
        path: 'settings',
        element: <SettingsRedirect />,
      },
      // Catch-all for unknown routes within the shell
      {
        path: '*',
        element: (
          <div style={errorStyles.container}>
            <div style={errorStyles.content}>
              <h2 style={errorStyles.title}>Page Not Found</h2>
              <p style={errorStyles.message}>
                The page you're looking for doesn't exist.
              </p>
              <a
                href={IS_TAURI ? '/events' : '/queue'}
                style={errorStyles.link}
                className="link-hover"
              >
                {IS_TAURI ? 'Return to Events' : 'Return to the Queue'}
              </a>
            </div>
          </div>
        ),
      },
]

export const router = createBrowserRouter([
  {
    path: '/',
    element: <AppShell />,
    errorElement: <RouteErrorBoundary />,
    children: IS_TAURI
      ? desktopRoutes
      : desktopRoutes.filter((route) => !isDesktopOnly(route.path)),
  },
])
